import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Globe, CheckCircle2, Lightbulb, HelpCircle, Target,
  ExternalLink, DollarSign, Package, ImageIcon, FileText, Wrench,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useProduct, useFAQs, useAssets } from '@/hooks/usePartnerships';
import { CategoryBadge } from '@/components/partnerships/CategoryBadge';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function ProductDetailPage() {
  const { t } = useTranslation();
  const { partner: slug, id: productId } = useParams<{ partner: string; id: string }>();
  const { data: product, isLoading } = useProduct(productId || '');
  const { data: allFaqs = [] } = useFAQs(product?.partnerId || '');
  const { data: allAssets = [] } = useAssets(product?.partnerId || '');

  if (isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  if (!product) return <div className="py-16 text-center text-muted-foreground">{t('partnerships.product_detail.not_found')}</div>;

  const relatedFaqs = product.relatedFaqIds?.length
    ? allFaqs.filter(f => product.relatedFaqIds!.includes(f.faqId))
    : [];
  const relatedAssets = product.relatedAssetIds?.length
    ? allAssets.filter(a => product.relatedAssetIds!.includes(a.assetId))
    : [];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={`/partnerships/${slug}`} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{product.name}</h1>
              <CategoryBadge category={product.category} />
            </div>
            {product.tagline && <p className="text-muted-foreground mt-1">{product.tagline}</p>}
          </div>
        </div>
        {product.driveUrl && (
          <a href={product.driveUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button className="gap-2">
              <ExternalLink className="h-4 w-4" />
              {t('partnerships.product_detail.view_in_drive')}
            </Button>
          </a>
        )}
      </div>

      {/* Pricing — destacado */}
      {product.pricing && (
        <Card className="p-4 border-primary/30 bg-primary/5 flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t('partnerships.product_detail.investment_label')}</p>
            <p className="font-semibold">{product.pricing}</p>
          </div>
        </Card>
      )}

      {/* Images */}
      {product.imageUrls && product.imageUrls.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {product.imageUrls.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block group relative overflow-hidden rounded-lg border bg-muted aspect-video">
              <img src={url} alt={`${product.name} visual ${i + 1}`} className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" onError={e => (e.currentTarget.style.display = 'none')} />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                <ExternalLink className="h-5 w-5 text-white" />
              </div>
            </a>
          ))}
        </div>
      )}

      {product.description && (
        <Card className="p-5">
          <p className="text-sm leading-relaxed">{product.description}</p>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {product.howItWorks.length > 0 && (
          <Card className="p-5 space-y-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              {t('partnerships.product_detail.how_it_works')}
            </h2>
            <ol className="space-y-2">
              {product.howItWorks.map((step, i) => (
                <li key={i} className="text-sm text-muted-foreground flex gap-2">
                  <span className="font-semibold text-foreground shrink-0">{i + 1}.</span>
                  {step}
                </li>
              ))}
            </ol>
          </Card>
        )}

        {product.keyBenefits.length > 0 && (
          <Card className="p-5 space-y-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('partnerships.product_detail.key_benefits')}
            </h2>
            <ul className="space-y-2">
              {product.keyBenefits.map((b, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {product.whenToUse && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" />
              {t('partnerships.product_detail.when_to_use')}
            </h2>
            <p className="text-sm text-muted-foreground">{product.whenToUse}</p>
          </Card>
        )}

        {product.whyItMatters && (
          <Card className="p-5 space-y-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-purple-500" />
              {t('partnerships.product_detail.why_it_matters')}
            </h2>
            <p className="text-sm text-muted-foreground">{product.whyItMatters}</p>
          </Card>
        )}
      </div>

      {product.deliverables && product.deliverables.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-indigo-500" />
            {t('partnerships.product_detail.deliverables')}
          </h2>
          <ul className="space-y-1">
            {product.deliverables.map((d, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                {d}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {(product.mediaRequirements || product.creativeRequirements) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {product.mediaRequirements && (
            <Card className="p-5 space-y-2">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Wrench className="h-4 w-4 text-orange-500" />
                {t('partnerships.product_detail.media_requirements')}
              </h2>
              <p className="text-sm text-muted-foreground">{product.mediaRequirements}</p>
            </Card>
          )}
          {product.creativeRequirements && (
            <Card className="p-5 space-y-2">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-pink-500" />
                {t('partnerships.product_detail.creative_requirements')}
              </h2>
              <p className="text-sm text-muted-foreground">{product.creativeRequirements}</p>
            </Card>
          )}
        </div>
      )}

      {product.bestPractices && (
        <Card className="p-5 space-y-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-teal-500" />
            {t('partnerships.product_detail.best_practices')}
          </h2>
          <p className="text-sm text-muted-foreground whitespace-pre-line">{product.bestPractices}</p>
        </Card>
      )}

      {product.availableMarkets.length > 0 && (
        <Card className="p-5 space-y-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('partnerships.product_detail.available_markets')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {product.availableMarkets.map(m => (
              <span key={m} className="text-xs px-2 py-1 rounded-md bg-muted">{m}</span>
            ))}
          </div>
        </Card>
      )}

      {relatedAssets.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold">{t('partnerships.product_detail.drive_materials')}</h2>
          <div className="space-y-2">
            {relatedAssets.map(a => (
              <a
                key={a.assetId}
                href={a.driveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-2 rounded-lg border hover:bg-muted transition-colors group"
              >
                <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.assetType}</p>
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
              </a>
            ))}
          </div>
        </Card>
      )}

      {relatedFaqs.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            {t('partnerships.product_detail.related_faqs')}
          </h2>
          <div className="space-y-3">
            {relatedFaqs.map(f => (
              <div key={f.faqId} className="space-y-1">
                <p className="text-sm font-medium">{f.question}</p>
                <p className="text-sm text-muted-foreground">{f.answer}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
